import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useMap, InfoWindow } from '@vis.gl/react-google-maps';
import { MarkerClusterer, Marker } from '@googlemaps/markerclusterer';
import GoogleMapMarkerV2 from './GoogleMapMarkerV2';
import GoogleMapMarkerDetail from './GoogleMapMarkerDetail';
import { IonButton, IonIcon } from '@ionic/react';
import { business, businessSharp, person, personSharp } from 'ionicons/icons';
import LoadingDots from './LoadingDots';

interface IProps {
    loading: boolean,
    buildings:any
};

const GoogleMapClusters:React.FC<IProps> = ({loading,buildings}) => {
    const map = useMap();
    const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
    const clusterer = useRef<MarkerClusterer | null>(null);
    const [selectedBuildingKey, setSelectedBuildingKey] = useState<string | null>(null);

    const selectedBuilding = useMemo(
      () =>
        buildings && selectedBuildingKey
          ? buildings.find((b:any) => b.uuid === selectedBuildingKey)!
          : null,
      [buildings, selectedBuildingKey]
    );

    useEffect(() => {
        if (!map) return;
        if (!clusterer.current) {
        clusterer.current = new MarkerClusterer({ map });
        }
    }, [map]);
    
    useEffect(() => {
        if (!clusterer.current) return;

        clusterer.current.clearMarkers();
        clusterer.current.addMarkers(Object.values(markers));
    }, [markers]);

    const setMarkerRef = (marker: Marker | null, key: string) => {
        if (marker && !markers[key]) {
        setMarkers((prev) => ({ ...prev, [key]: marker }));
        } else if (!marker && markers[key]) {
        setMarkers((prev) => {
            const newMarkers = { ...prev };
            delete newMarkers[key];
            return newMarkers;
        });
        }
    };
    
    const handleInfoWindowClose = useCallback(() => {
        setSelectedBuildingKey(null);
    }, []);

    const handleMarkerClick = useCallback((b: any) => {
        setSelectedBuildingKey(b.uuid);
    }, []);

    if(loading){
      return <div
            style={{
              position: "absolute",
              width: "100%",
              border: "black solid 3px",
              top: "0px",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              verticalAlign: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
            }}
          >
            <LoadingDots style={{ position: "absolute", bottom: "50%" }} />
          </div>
    }


    return (
        <>
            {buildings.map((building: any) => {
              const Addr = `${building.building_address}, ${building.building_city}, ${building.building_state} ${building.building_zipcode}`;
              return (
                <GoogleMapMarkerV2
                  building={building}
                  Address={Addr}
                  key={building.uuid}
                  setMarkerRef={setMarkerRef}
                  onClick={handleMarkerClick}
                />
              );
            })}

            {selectedBuildingKey && (
            <InfoWindow
            anchor={markers[selectedBuildingKey]}
            onClose={handleInfoWindowClose}
            className="markerInfo"
            headerContent={selectedBuilding.building_name}
            >
                <GoogleMapMarkerDetail building={selectedBuilding} address={`${selectedBuilding.building_address}, ${selectedBuilding.building_city}, ${selectedBuilding.building_state} ${selectedBuilding.building_zipcode}`} />
            <div className="right-buttons">
              <IonButton
                title="Building Page"
                size="small"
                href={`/?id=${selectedBuilding.uuid}&sec=buildings`}
              >
                <IonIcon slot="icon-only" ios={businessSharp} md={business} />
              </IonButton>
              <IonButton
                title="Customer Page"
                size="small"
                href={`/?id=${selectedBuilding.cust_uuid}&sec=customers`}
              >
                <IonIcon slot="icon-only" ios={personSharp} md={person} />
              </IonButton>
            </div>
            </InfoWindow>
            )}
        </>
    )
};

export default GoogleMapClusters;